import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import { useConfirm } from '@/hooks/useConfirm'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { LIGHT_LEVEL_ICONS, HUMIDITY_LEVEL_ICONS, EnumIcon } from '@/lib/icons'

interface Photo {
  id: number
  url: string
  thumbnail_url: string
}

interface Location {
  id: number
  name: string
  light_level: string | null
  light_level_pl: string | null
  humidity_level: string | null
  humidity_level_pl: string | null
  temperature_range: string | null
  has_heating_nearby: boolean
  notes: string | null
  photo: Photo | null
  plants: { id: number; name: string }[]
}

interface Props {
  location: Location
}

function LocationsShow({ location }: Props) {
  const { pending, confirm, handleConfirm, handleCancel } = useConfirm()

  const handleDelete = () => {
    confirm('Na pewno usunąć to miejsce?', () => router.delete(`/locations/${location.id}`))
  }

  return (
    <AtlasPage>
      <PageTitle title={location.name} />
      <Separator />

      {/* Photo */}
      {location.photo && (
        <div className="flex justify-center mb-md">
          <img
            src={location.photo.url}
            alt={location.name}
            className="max-h-64 object-contain border border-frame/20"
          />
        </div>
      )}

      {/* Details */}
      <div className="grid grid-cols-1 gap-sm sm:grid-cols-2 text-sm">
        <DataRow label="Światło">
          {location.light_level_pl ? (
            <span className="inline-flex items-center gap-xs">
              <EnumIcon entry={location.light_level ? LIGHT_LEVEL_ICONS[location.light_level] : undefined} size={14} />
              {location.light_level_pl}
            </span>
          ) : '—'}
        </DataRow>
        <DataRow label="Wilgotność">
          {location.humidity_level_pl ? (
            <span className="inline-flex items-center gap-xs">
              <EnumIcon entry={location.humidity_level ? HUMIDITY_LEVEL_ICONS[location.humidity_level] : undefined} size={14} />
              {location.humidity_level_pl}
            </span>
          ) : '—'}
        </DataRow>
        <DataRow label="Temperatura">{location.temperature_range || '—'}</DataRow>
        <DataRow label="Ogrzewanie w pobliżu">{location.has_heating_nearby ? 'Tak' : 'Nie'}</DataRow>
        {location.notes && (
          <div className="sm:col-span-2">
            <DataRow label="Notatki">{location.notes}</DataRow>
          </div>
        )}
      </div>

      {/* Plants in this location */}
      {location.plants.length > 0 && (
        <>
          <Separator />
          <h2 className="font-display text-lg text-center uppercase tracking-wider text-ink mb-sm">
            Rośliny
          </h2>
          <ul className="space-y-xs">
            {location.plants.map((plant) => (
              <li key={plant.id} className="text-center">
                <Link
                  href={`/plants/${plant.id}`}
                  className="font-label text-botanical underline decoration-botanical/30 hover:decoration-botanical"
                >
                  {plant.name}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      <Separator />
      <div className="flex items-center justify-center gap-sm">
        <Button href={`/locations/${location.id}/edit`} variant="secondary">Edytuj</Button>
        <Button variant="danger" onClick={handleDelete}>Usuń</Button>
      </div>

      <PageNumber number={11} />
      {pending && <ConfirmModal message={pending.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
    </AtlasPage>
  )
}

function DataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-sm">
      <span className="font-label text-xs text-frame uppercase tracking-wide min-w-[8rem]">
        {label}:
      </span>
      <span className="text-ink">{children}</span>
    </div>
  )
}

LocationsShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default LocationsShow
